import { OCR_CONFIDENCE_THRESHOLD } from "@/services/ocrService"

export interface DropdownOption {
  label: string
  value?: string
}

export type MatchStrategy = 'exact' | 'normalized' | 'numeric' | 'unit' | 'contains' | 'fuzzy'

export interface MatchResult {
  matched: boolean
  matchedOption: DropdownOption | null
  confidence: number
  strategy: MatchStrategy
}

const UNIT_ALIASES: [RegExp, string][] = [
  [/\bpounds?\b/gi, 'lb'],
  [/\blbs\b/gi, 'lb'],
]

function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/,/g, '')
    .replace(/[^\w\s.]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeUnits(s: string): string {
  let result = s
  for (const [pattern, replacement] of UNIT_ALIASES) {
    result = result.replace(pattern, replacement)
  }
  return result.replace(/\s+/g, ' ').trim()
}

function extractNumeric(s: string): number | null {
  const cleaned = s.replace(/,/g, '')
  const match = cleaned.match(/[\d]+(?:\.\d+)?/)
  if (!match) return null
  const n = parseFloat(match[0])
  return isNaN(n) ? null : n
}

function levenshtein(a: string, b: string): number {
  const m = a.length
  const n = b.length
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  )
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1])
    }
  }
  return dp[m][n]
}

function levenshteinSimilarity(a: string, b: string): number {
  if (!a && !b) return 1
  const dist = levenshtein(a, b)
  return 1 - dist / Math.max(a.length, b.length)
}

const NO_MATCH: MatchResult = { matched: false, matchedOption: null, confidence: 0, strategy: 'fuzzy' }

export function matchDropdownValue(
  rawOcrValue: string,
  options: DropdownOption[],
  confidenceThreshold: number = OCR_CONFIDENCE_THRESHOLD,
): MatchResult {
  if (!rawOcrValue?.trim() || options.length === 0) return NO_MATCH

  const candidates = options.filter((o) => !o.label.startsWith('Select'))
  if (candidates.length === 0) return NO_MATCH

  const raw = rawOcrValue.trim()
  const rawLower = raw.toLowerCase()

  // Tier 1: exact (case-sensitive)
  const exact = candidates.find((o) => o.label === raw)
  if (exact) return { matched: true, matchedOption: exact, confidence: 1.0, strategy: 'exact' }

  // Tier 2: normalized — lowercase, strip commas/punctuation, collapse spaces
  const rawNorm = normalize(raw)
  const normalized = candidates.find((o) => normalize(o.label) === rawNorm)
  if (normalized) return { matched: true, matchedOption: normalized, confidence: 0.95, strategy: 'normalized' }

  // Tier 3: numeric — extract numeric value and compare ignoring units/formatting
  const rawNum = extractNumeric(raw)
  if (rawNum !== null) {
    const numericMatch = candidates.find((o) => {
      const optNum = extractNumeric(o.label)
      return optNum !== null && optNum === rawNum
    })
    if (numericMatch) {
      const result: MatchResult = { matched: true, matchedOption: numericMatch, confidence: 0.9, strategy: 'numeric' }
      if (result.confidence >= confidenceThreshold) return result
    }
  }

  // Tier 4: unit — normalize units then compare numeric+unit combination
  const rawUnitNorm = normalizeUnits(normalize(raw))
  const unitMatch = candidates.find((o) => normalizeUnits(normalize(o.label)) === rawUnitNorm)
  if (unitMatch) {
    const result: MatchResult = { matched: true, matchedOption: unitMatch, confidence: 0.85, strategy: 'unit' }
    if (result.confidence >= confidenceThreshold) return result
  }

  // Tier 5: contains — option label contains raw or vice versa
  const containsFwd = candidates.find((o) => o.label.toLowerCase().includes(rawLower))
  if (containsFwd) {
    const result: MatchResult = { matched: true, matchedOption: containsFwd, confidence: 0.75, strategy: 'contains' }
    if (result.confidence >= confidenceThreshold) return result
  }
  const containsRev = candidates.find((o) => rawLower.includes(o.label.toLowerCase()))
  if (containsRev) {
    const result: MatchResult = { matched: true, matchedOption: containsRev, confidence: 0.75, strategy: 'contains' }
    if (result.confidence >= confidenceThreshold) return result
  }

  // Tier 6: fuzzy — Levenshtein similarity, pick best candidate
  let bestOption: DropdownOption | null = null
  let bestSimilarity = 0
  for (const opt of candidates) {
    const sim = levenshteinSimilarity(rawNorm, normalize(opt.label))
    if (sim > bestSimilarity) {
      bestSimilarity = sim
      bestOption = opt
    }
  }
  if (bestOption && bestSimilarity >= confidenceThreshold) {
    return { matched: true, matchedOption: bestOption, confidence: bestSimilarity, strategy: 'fuzzy' }
  }

  return NO_MATCH
}
